import React, { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePostHog } from '@posthog/react';
import { ONBOARDING_EVENT, capture } from '../analytics/posthog';
import styles from './CareerShiftScenarioMap.module.css';
import loadingStyles from './shared/Loading.module.css';
import { logger } from '../config';
import { getToken, getOnboardingAIData, handleApiError, updateV2OnboardingAiResult } from '../utils/api';
import {
  getDurationValue,
  getSpouseAgeText,
  getHealthcareCostsText,
  getSocialSecurityText,
  getLongevityText,
  getFinancialIndependenceText,
  normalizeOnboardingProgressKeys,
  onboardingProgressFromV2OnboardingAiResult,
  calculateAllFinancialDataAI,
  calculatedFinancialDataToV2OnboardingAiResult
} from '../utils/onboardingUtils';
import {
  calculateAgeFromBirthYear,
  formatNumberEnUS,
  FINANCIAL_CONSTANTS,
  parseNumericValue,
  isMarried
} from '../utils/helper';

const { INFLATION_RATE, INVESTMENT_RETURN_RATE, LIFE_EXPECTANCY, SOCIAL_SECURITY_BENEFIT_AGE } =
  FINANCIAL_CONSTANTS;

const MIN_LOADING_MS = 2500;
const STROKE_COLOR = '#044766';
const COLLEGE_DURATION = 4;
const YEAR_MIN = 2000;
const YEAR_MAX = 2100;

const FALLBACK = {
  duration: '1 year',
  spouseAge: "While your spouse's/partner's age is not specified",
  healthcare: 'Your healthcare costs approximately will be $197,500 lifetime for you.',
  socialSecurity: `Social Security benefits are assumed to start for you at age ${SOCIAL_SECURITY_BENEFIT_AGE}.`,
  longevity: `For planning purposes, this scenario assumes you live up to ${LIFE_EXPECTANCY} years.`,
  financialIndependence: 'The goal is to achieve financial independence and retire.',
  kidsCollege: 'This expense is not currently applicable.'
};

/** V2 row is `{ financial_profile, updated_at, ...scenario fields }`; map to the same shape as `calculateAllFinancialDataAI`. */
function v2OnboardingAiResultToFinancialData(v2Result) {
  const rawProgress = onboardingProgressFromV2OnboardingAiResult(v2Result);
  if (!rawProgress) return null;

  const onboardingProgress = normalizeOnboardingProgressKeys(rawProgress);
  const derived = {
    duration: getDurationValue(onboardingProgress) || FALLBACK.duration,
    spouseAgeText: getSpouseAgeText(onboardingProgress) || FALLBACK.spouseAge,
    healthcareCostsText: getHealthcareCostsText(onboardingProgress) || FALLBACK.healthcare,
    socialSecurityText: getSocialSecurityText(onboardingProgress) || FALLBACK.socialSecurity,
    longevityText: getLongevityText(onboardingProgress) || FALLBACK.longevity,
    financialIndependenceText:
      getFinancialIndependenceText(onboardingProgress) || FALLBACK.financialIndependence
  };

  return {
    duration: v2Result.duration ?? derived.duration,
    spouseAgeText: v2Result.spouseAgeText ?? derived.spouseAgeText,
    healthcareCostsText: v2Result.healthcareCostsText ?? derived.healthcareCostsText,
    socialSecurityText: v2Result.socialSecurityText ?? derived.socialSecurityText,
    longevityText: v2Result.longevityText ?? derived.longevityText,
    financialIndependenceText: v2Result.financialIndependenceText ?? derived.financialIndependenceText,
    onboardingProgress
  };
}

function toFinancialData(data) {
  if (!data || typeof data !== 'object' || Array.isArray(data)) return null;
  const payload = v2OnboardingAiResultToFinancialData(data) || data;
  if (
    payload.duration == null &&
    payload.spouseAgeText == null &&
    payload.onboardingProgress == null
  ) {
    return null;
  }
  return {
    ...payload,
    onboardingProgress: normalizeOnboardingProgressKeys(payload.onboardingProgress || {})
  };
}

/** Chat list from GET v2/onboarding-ai (`v2_onboarding_ai.chat` in JSON). */
function getOnboardingChatFromApiData(data) {
  const chat = data?.v2_onboarding_ai?.chat;
  return Array.isArray(chat) ? chat : [];
}

/** Wait for fonts + two frames so card heights/borders are measured before first paint. */
function waitUntilPaintReady() {
  const fontsReady = document.fonts?.ready ?? Promise.resolve();
  return Promise.resolve(fontsReady)
    .catch(() => {})
    .then(
      () =>
        new Promise((resolve) => {
          requestAnimationFrame(() => requestAnimationFrame(resolve));
        })
    );
}

async function persistOnboardingAiResult(token, calculatedData, navigate) {
  const result = await updateV2OnboardingAiResult(
    {
      token,
      v2OnboardingAiResult: calculatedFinancialDataToV2OnboardingAiResult(calculatedData)
    },
    navigate
  );
  if (!result.ok) {
    logger.warn('Failed to save calculated data:', result.message || 'Unknown error');
  }
}

/** SVG connection lines: vertical/horizontal lines and dots between scenario cards */
const CONNECTION_LINES = [
  { type: 'line', x1: 250, y1: 200, x2: 250, y2: 400 },
  { type: 'line', x1: 250, y1: 400, x2: 720, y2: 400 },
  { type: 'circle', cx: 250, cy: 400 },
  { type: 'circle', cx: 720, cy: 400 },
  { type: 'line', x1: 720, y1: 400, x2: 720, y2: 670 },
  { type: 'circle', cx: 250, cy: 670 },
  { type: 'circle', cx: 720, cy: 670 },
  { type: 'line', x1: 250, y1: 670, x2: 720, y2: 670 },
  { type: 'line', x1: 250, y1: 670, x2: 250, y2: 950 },
  { type: 'circle', cx: 250, cy: 950 },
  { type: 'circle', cx: 720, cy: 950 },
  { type: 'line', x1: 250, y1: 950, x2: 720, y2: 950 },
  { type: 'line', x1: 720, y1: 1220, x2: 720, y2: 950 },
  { type: 'circle', cx: 720, cy: 880 },
  { type: 'line', x1: 720, y1: 1220, x2: 250, y2: 1220 },
  { type: 'circle', cx: 720, cy: 1220 },
  { type: 'circle', cx: 250, cy: 1220 },
  { type: 'line', x1: 250, y1: 1500, x2: 250, y2: 1220 },
  { type: 'circle', cx: 250, cy: 1500 },
  { type: 'circle', cx: 720, cy: 1500 },
  { type: 'line', x1: 250, y1: 1500, x2: 720, y2: 1500 },
  { type: 'line', x1: 720, y1: 1760, x2: 720, y2: 1500 },
  { type: 'circle', cx: 720, cy: 1760 },
  { type: 'circle', cx: 250, cy: 1760 },
  { type: 'line', x1: 720, y1: 1760, x2: 250, y2: 1760 }
];

function parseCollegeYear(item) {
  if (item == null) return NaN;
  const raw = typeof item === 'object' && 'year' in item ? item.year : item;
  const match = String(raw ?? '').match(/\b(20\d{2})\b/);
  return match ? parseInt(match[1], 10) : NaN;
}

function isValidYear(year) {
  return !isNaN(year) && year >= YEAR_MIN && year <= YEAR_MAX;
}

function formatChildrenForYear(childNumbers, year) {
  const sorted = [...childNumbers].sort((a, b) => a - b);
  if (sorted.length === 1) return `Child ${sorted[0]} in ${year}`;
  if (sorted.length === 2) return `Children ${sorted[0]} and ${sorted[1]} in ${year}`;
  const last = sorted.pop();
  return `Children ${sorted.join(', ')}, and ${last} in ${year}`;
}

function buildKidsCollegeText(sourceProgress) {
  try {
    const childrenNumber = parseInt(sourceProgress['children_number'], 10) || 0;
    const childrenCollegeYears = sourceProgress['children_college_years'] || [];
    const childrenCollegeSavings = parseNumericValue(sourceProgress['children_college_savings']) || 0;

    if (childrenNumber === 0 || !Array.isArray(childrenCollegeYears) || childrenCollegeYears.length === 0) {
      return FALLBACK.kidsCollege;
    }

    // Keep per-child indexing so "Child N" references remain correct even if only some children have valid years.
    const yearToChildNumbers = {};
    for (let i = 0; i < childrenCollegeYears.length; i++) {
      const year = parseCollegeYear(childrenCollegeYears[i]);
      if (!isValidYear(year)) continue;
      if (!yearToChildNumbers[year]) yearToChildNumbers[year] = [];
      yearToChildNumbers[year].push(i + 1);
    }

    const parsedYears = Object.keys(yearToChildNumbers)
      .map((y) => parseInt(y, 10))
      .sort((a, b) => a - b);
    if (parsedYears.length === 0) return FALLBACK.kidsCollege;

    const currentYear = new Date().getFullYear();
    const baseText = `We have allocated a total of $${Math.round(childrenCollegeSavings / 1000)}K for your kids' college education fund`;
    const futureYears = parsedYears.filter((y) => y > currentYear);
    const currentYears = parsedYears.filter((y) => y <= currentYear && y + COLLEGE_DURATION >= currentYear);

    if (futureYears.length === 0 && currentYears.length > 0) {
      if (currentYears.length === 1) {
        return `${baseText}. Your child is currently in college and will graduate in ${currentYears[0] + COLLEGE_DURATION}.`;
      }
      return `${baseText}. ${currentYears.length} children are currently in college.`;
    }

    if (futureYears.length === 0) return FALLBACK.kidsCollege;

    const childYearEntries = futureYears.map((year) => formatChildrenForYear(yearToChildNumbers[year] || [], year));
    const totalChildren = futureYears.reduce((acc, year) => acc + (yearToChildNumbers[year] || []).length, 0);

    let childrenDetails;
    if (futureYears.length === 1) {
      childrenDetails =
        totalChildren === 1
          ? `1 child will start college in ${futureYears[0]}`
          : `${totalChildren} children will start college in ${futureYears[0]}`;
    } else {
      const lastEntry = childYearEntries[childYearEntries.length - 1];
      const otherEntries = childYearEntries.slice(0, -1).join(', ');
      childrenDetails = `${totalChildren} children will start college: ${otherEntries}, and ${lastEntry}`;
    }

    return `${baseText}. ${childrenDetails}.`;
  } catch (err) {
    logger.error('Error generating kids college text:', err);
    return FALLBACK.kidsCollege;
  }
}

function buildScenarioCopy(financialData) {
  const progress = financialData?.onboardingProgress || {};
  const partnerIncome = parseNumericValue(progress.monthly_net_income_partner) || 0;
  const yob = progress.your_year_of_birth;
  const text = (aiValue, computed, fallback) => aiValue || computed || fallback;

  return {
    userAge: yob ? calculateAgeFromBirthYear(yob) : null,
    duration: text(financialData?.duration, getDurationValue(progress), FALLBACK.duration),
    spouseAge: !isMarried(progress.family_situation)
      ? FALLBACK.spouseAge
      : text(financialData?.spouseAgeText, getSpouseAgeText(progress), FALLBACK.spouseAge),
    incomeDuring: Math.round(
      (parseNumericValue(progress.monthly_net_income_during_career_shift) || 0) + partnerIncome
    ),
    incomeAfter: Math.round(
      (parseNumericValue(progress.monthly_net_income_after_career_shift) || 0) + partnerIncome
    ),
    kidsCollege: buildKidsCollegeText(progress),
    annualInvestment: Math.round(parseNumericValue(progress.total_retirement_account_each_year) || 0),
    healthcare: text(financialData?.healthcareCostsText, getHealthcareCostsText(progress), FALLBACK.healthcare),
    socialSecurity: text(
      financialData?.socialSecurityText,
      getSocialSecurityText(progress),
      FALLBACK.socialSecurity
    ),
    longevity: text(financialData?.longevityText, getLongevityText(progress), FALLBACK.longevity),
    financialIndependence: text(
      financialData?.financialIndependenceText,
      getFinancialIndependenceText(progress),
      FALLBACK.financialIndependence
    )
  };
}

/** Render stored copy that may include a single `<br />` (Social Security). */
function CardBody({ children }) {
  if (typeof children !== 'string' || !children.includes('<br')) {
    return <p className={styles.cardBody}>{children}</p>;
  }
  return (
    <p className={styles.cardBody}>
      {children.split(/<br\s*\/?>/i).map((part, i) => (
        <Fragment key={i}>
          {i > 0 && <br />}
          {part}
        </Fragment>
      ))}
    </p>
  );
}

function ConnectionLines() {
  return (
    <svg
      className={styles.connectionLines}
      viewBox="0 0 1000 2000"
      preserveAspectRatio="none"
      aria-hidden
    >
      {CONNECTION_LINES.map((item, i) =>
        item.type === 'line' ? (
          <line
            key={`line-${i}`}
            x1={item.x1}
            y1={item.y1}
            x2={item.x2}
            y2={item.y2}
            stroke={STROKE_COLOR}
            strokeWidth="2"
            strokeDasharray={item.y1 === item.y2 ? '4,4' : '4,6'}
          />
        ) : (
          <circle key={`circle-${i}`} cx={item.cx} cy={item.cy} r="7" fill={STROKE_COLOR} />
        )
      )}
    </svg>
  );
}

function ScenarioCard({ title, icon, colorClass, side, tooltip, children, step }) {
  const isLeft = side === 'left';
  return (
    <div
      className={[
        styles.scenarioCardItem,
        styles[colorClass],
        !isLeft && styles.scenarioCardRight
      ].filter(Boolean).join(' ')}
    >
      <div className={styles.cardHeader}>
        <div className={styles.cardIcon}>{icon}</div>
        <h3 className={styles.cardTitleRow}>
          <span className={styles.cardTitleText}>{title}</span>
          {tooltip && (
            <div
              className={[
                styles.tooltipContainer,
                isLeft && styles.tooltipContainerLeft
              ].filter(Boolean).join(' ')}
            >
              <div className={styles.tooltipIcon}>i</div>
              <div className={styles.tooltipPopup}>{tooltip}</div>
            </div>
          )}
        </h3>
        {step != null && <span className={styles.stepBadge}>{step}</span>}
      </div>
      <CardBody>{children}</CardBody>
    </div>
  );
}

function LoadingView() {
  return (
    <div className={styles.loadingScreen}>
      <div className={styles.loadingContent}>
        <div className={styles.loadingSpinnerWrapper}>
          <div className={loadingStyles['loading-spinner']}>
            <div className={loadingStyles['spinner-ring']} />
            <div className={loadingStyles['spinner-ring']} />
            <div className={loadingStyles['spinner-ring']} />
          </div>
        </div>
        <h2 className={styles.loadingTitle}>Creating your Career Shift and Longevity Plan</h2>
        <p className={styles.loadingSubtitle}>
          We're analyzing your financial data and creating a personalized career shift plan...
        </p>
        <div className={styles.loadingDots}>
          <span />
          <span />
          <span />
        </div>
      </div>
    </div>
  );
}

const CareerShiftScenarioMap = () => {
  const navigate = useNavigate();
  const posthog = usePostHog();
  const hasLoadedRef = useRef(false);
  const mountTimeRef = useRef(Date.now());
  const [isLoading, setIsLoading] = useState(true);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [onboardingDone, setOnboardingDone] = useState(false);
  const [financialData, setFinancialData] = useState(null);

  useEffect(() => {
    const loadOnboardingData = async () => {
      if (hasLoadedRef.current) return;
      hasLoadedRef.current = true;

      try {
        const token = getToken();
        if (!token) {
          navigate('/login');
          return;
        }

        const response = await getOnboardingAIData(token, navigate);
        if (!response?.success || !response.data) return;

        const { data } = response;
        setOnboardingDone(Boolean(data.onboarding_ai_done));

        const cachedRaw = data.v2_onboarding_ai_result;
        if (cachedRaw != null && typeof cachedRaw === 'object' && !Array.isArray(cachedRaw)) {
          const cached = toFinancialData(cachedRaw);
          if (cached) setFinancialData(cached);
          return;
        }

        const chatData = getOnboardingChatFromApiData(data);
        if (chatData.length === 0) return;

        try {
          const calculated = await calculateAllFinancialDataAI(chatData);
          await persistOnboardingAiResult(token, calculated, navigate);
          setFinancialData(toFinancialData(calculated));
        } catch (err) {
          logger.error('Error calculating financial data:', err);
          setFinancialData(toFinancialData(data.v2_onboarding_ai_result));
        }
      } catch (err) {
        logger.error('Error loading onboarding data:', err);
        handleApiError(err, navigate);
      } finally {
        setDataLoaded(true);
      }
    };

    loadOnboardingData();
  }, [navigate]);

  useEffect(() => {
    if (!dataLoaded) return;
    let cancelled = false;

    const revealWhenReady = async () => {
      const remaining = Math.max(0, MIN_LOADING_MS - (Date.now() - mountTimeRef.current));
      if (remaining > 0) {
        await new Promise((resolve) => setTimeout(resolve, remaining));
      }
      await waitUntilPaintReady();
      if (!cancelled) setIsLoading(false);
    };

    revealWhenReady();
    return () => {
      cancelled = true;
    };
  }, [dataLoaded]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    capture(posthog, ONBOARDING_EVENT, { step: 'career_shift_map_opened' });
    const mountTime = mountTimeRef.current;
    return () => {
      const timeSpentSeconds = Math.round((Date.now() - mountTime) / 1000);
      capture(posthog, ONBOARDING_EVENT, {
        step: 'career_shift_map_time_spent',
        time_spent_seconds: timeSpentSeconds
      });
    };
  }, [posthog]);

  useEffect(() => {
    if (isLoading || onboardingDone) return;
    navigate('/v2-onboarding-agent');
  }, [isLoading, onboardingDone, navigate]);

  const handleDashboard = useCallback(() => {
    capture(posthog, ONBOARDING_EVENT, { step: 'career_shift_map_view_forecast' });
    navigate('/dashboard/forecast');
  }, [navigate, posthog]);

  const handleEditProfile = useCallback(() => {
    capture(posthog, ONBOARDING_EVENT, { step: 'career_shift_map_edit_profile' });
    navigate('/dashboard/copilot');
  }, [navigate, posthog]);

  if (isLoading) return <LoadingView />;
  if (!onboardingDone) return null;

  const copy = buildScenarioCopy(financialData);
  const tooltipInvestment = (
    <>
      Inflation growth rate is an estimate based on future projection of 10 years and a midpoint of 2.3% annually has been considered for calculation. Source:{' '}
      <a href="https://www.statista.com" target="_blank" rel="noopener noreferrer" className={styles.tooltipLink}>
        Statista.com
      </a>
    </>
  );
  const tooltipSocialSecurity = (
    <>
      Social Security average withdrawal age of 67 is considered. The $2,500 benefit is as of current and may change in the future. For more accurate information you may refer to{' '}
      <a href="https://www.ssa.gov" target="_blank" rel="noopener noreferrer" className={styles.tooltipLink}>
        www.ssa.gov
      </a>
    </>
  );

  const cardRows = [
    [
      {
        title: 'Career Shift',
        icon: '🛡️',
        colorClass: 'cardTeal',
        side: 'left',
        step: 1,
        children: (
          <>
            You're planning a {copy.duration} career shift
            {copy.userAge != null ? `, starting at age ${copy.userAge}` : ''}.
            <br />
            {copy.spouseAge}. You anticipate total monthly household work income of $
            {formatNumberEnUS(copy.incomeDuring)} during the shift and $
            {formatNumberEnUS(copy.incomeAfter)} after the shift.
          </>
        )
      },
      {
        title: 'Kids in College',
        icon: '😊',
        colorClass: 'cardPink',
        side: 'right',
        step: 2,
        children: copy.kidsCollege
      }
    ],
    [
      {
        title: 'Investment Growth',
        icon: '💰',
        colorClass: 'cardOrange',
        side: 'left',
        step: 3,
        tooltip: tooltipInvestment,
        children: `Assume ${(INVESTMENT_RETURN_RATE * 100).toFixed(0)}% rate of return, with an inflation rate of ${(INFLATION_RATE * 100).toFixed(1)}%. Contributing $${formatNumberEnUS(copy.annualInvestment)} annually to your investments.`
      },
      {
        title: 'Financial Independence',
        icon: '💵',
        colorClass: 'cardGreen',
        side: 'right',
        step: 4,
        children: copy.financialIndependence
      }
    ],
    [
      {
        title: 'Social Security',
        icon: '📄',
        colorClass: 'cardBlue',
        side: 'left',
        step: 5,
        tooltip: tooltipSocialSecurity,
        children: copy.socialSecurity
      },
      {
        title: 'Health Care Costs',
        icon: '🏥',
        colorClass: 'cardPurple',
        side: 'right',
        step: 6,
        tooltip: <>Best case scenario and current cost, will change depending upon each individual's health condition.</>,
        children: copy.healthcare
      }
    ],
    [
      {
        title: 'Longevity',
        icon: '⏰',
        colorClass: 'cardGold',
        side: 'left',
        step: 7,
        children: copy.longevity
      }
    ]
  ];

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        <div className={styles.mainContainer}>
          <h1 className={styles.title}>Career Shift Scenario Map</h1>
          <h2 className={styles.subtitle}>
            Reese has created this career shift scenario based on the details you've shared and standard financial planning benchmarks.
            <br />
            You can update these assumptions in your profile anytime to better reflect your situation.
          </h2>

          <div className={styles.scenarioGrid}>
            <ConnectionLines />
            {cardRows.map((row, i) => (
              <div
                key={row[0].step}
                className={i === cardRows.length - 1 ? `${styles.gridRow} ${styles.gridRowLast}` : styles.gridRow}
              >
                {row.map((card) => (
                  <ScenarioCard key={card.step} {...card} />
                ))}
              </div>
            ))}
            <div className={styles.buttonContainer}>
              <button type="button" className={styles.editProfileButton} onClick={handleEditProfile}>
                Edit Profile
              </button>
              <button type="button" className={styles.dashboardButton} onClick={handleDashboard}>
                View My Forecast
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CareerShiftScenarioMap;
